import React, { useCallback, useEffect, useState } from 'react';
import api from '../api/api';
import { contextError } from '../utils/operatingContext';
import './OperatingGroupsSettings.css';

type Group = { id: number; name: string; controlledAccess: boolean };
export type RestaurantAccessContext = {
  systemAdministrator: boolean; selectedGroupId: number | null; allGroupsOverview?: boolean; groups: Group[];
};
type Member = { userId: string; login: string; isActive: boolean; manageAccess: boolean; version: number };
type Resource = { resourceType: string; id: number; name: string };
type Details = { members: Member[]; resources: Resource[] };
type Grant = {
  id: number; warehouseId: number | null; kitchenId: number | null; venueId: number | null;
  effectiveFrom: string; effectiveTo: string | null; cancelled: boolean;
};
type Grants = { userId: string; version: number; grants: Grant[] };
const today = () => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Kyiv', year: 'numeric', month: '2-digit', day: '2-digit',
}).format(new Date());

export default function RestaurantAccessSettings({ canEdit }: { canEdit: boolean }) {
  const [context, setContext] = useState<RestaurantAccessContext | null>(null);
  const [groupId, setGroupId] = useState('');
  const [details, setDetails] = useState<Details | null>(null);
  const [login, setLogin] = useState('');
  const [manage, setManage] = useState(false);
  const [memberId, setMemberId] = useState('');
  const [grants, setGrants] = useState<Grants | null>(null);
  const [resource, setResource] = useState('');
  const [newResource, setNewResource] = useState({ name: '', resourceType: 'warehouse', code: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const root = `/operating-contexts/restaurant-access/${groupId}`;
  const loadContext = useCallback(async () => {
    try {
      const { data } = await api.get<RestaurantAccessContext>('/operating-contexts/restaurant-access');
      setContext(data);
      if (!data.systemAdministrator) setGroupId(data.selectedGroupId ? String(data.selectedGroupId) : '');
    } catch (failure: unknown) { setContext(null); setError(contextError(failure)); }
  }, []);
  useEffect(() => { void loadContext(); }, [loadContext]);
  useEffect(() => {
    let active = true;
    setDetails(null); setGrants(null); setMemberId(''); setResource(''); setError('');
    if (groupId) {
      api.get<Details>(root).then(({ data }) => { if (active) setDetails(data); })
        .catch((failure: unknown) => { if (active) setError(contextError(failure)); });
    }
    return () => { active = false; };
  }, [groupId, root]);
  useEffect(() => {
    let active = true;
    setGrants(null);
    if (memberId && groupId) {
      api.get<Grants>(`${root}/users/${encodeURIComponent(memberId)}`)
        .then(({ data }) => { if (active) setGrants(data); })
        .catch((failure: unknown) => { if (active) setError(contextError(failure)); });
    }
    return () => { active = false; };
  }, [memberId, groupId, root]);
  const mutate = async (action: () => Promise<unknown>) => {
    setBusy(true); setError(''); setNotice(''); setGrants(null);
    let committed = false;
    try {
      await action();
      committed = true;
      setMemberId(''); setResource('');
      setDetails((await api.get<Details>(root)).data);
      setNotice('Зміни збережено. Доступ перевіряється з наступного запиту; історичні документи не змінено.');
    } catch (failure: unknown) {
      setDetails(null);
      setError(`${committed ? 'Зміни збережено, але оновлення не вдалося. ' : ''}${contextError(failure)} Оновіть панель перед наступною зміною.`);
    } finally { setBusy(false); }
  };
  const saveMember = (member: Member, isActive: boolean) => {
    if (!isActive && !window.confirm(`Відкликати доступ «${member.login}» до цього ресторану та завершити його локальні grants?`)) return;
    void mutate(() => api.put(`${root}/members`, {
      login: member.login, version: member.version, isActive, manageAccess: member.manageAccess,
    }));
  };
  const saveGrant = (closeId?: number) => {
    if (!grants) return;
    const [resourceType, id] = resource.split(':');
    if (closeId && !window.confirm('Відкликати цей доступ із сьогодні?')) return;
    void mutate(() => api.put(`${root}/users/${encodeURIComponent(grants.userId)}`, {
      version: grants.version, unrestricted: false, allowLegacyUnscoped: false,
      addGrants: closeId ? [] : [{ resourceType, resourceId: Number(id), effectiveFrom: today(), effectiveTo: null }],
      closeGrants: closeId ? [{ id: closeId, effectiveTo: today() }] : [],
    }));
  };
  return <section className="operating-groups-settings" aria-label="Локальний доступ ресторану">
    <h3>Підключення ресторану й локальний доступ</h3>
    <p>Новий ресторан порожній: без товарів, персоналу, залишків, зарплат і чужих налаштувань.
      Технічний доступ не створює працевлаштування, HR-права, системні ролі або пароль.</p>
    {error && <p role="alert">{error}</p>}{notice && <p role="status">{notice}</p>}
    <button type="button" disabled={busy} onClick={() => { setGroupId(''); setDetails(null); void loadContext(); }}>Оновити панель доступу</button>
    {context && <label>Ресторан для налаштування
      <select value={groupId} disabled={busy || !context.systemAdministrator} onChange={e => setGroupId(e.target.value)}>
        <option value="">Оберіть ресторан</option>
        {context.groups.filter(x => x.controlledAccess && (context.systemAdministrator || x.id === context.selectedGroupId))
          .map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
      </select>
    </label>}
    {context && !context.systemAdministrator && !details && <p>Спочатку оберіть ресторан у верхній панелі. Потрібне окреме право керування локальним доступом.</p>}
    {details && <>
      <form className="operating-groups-create" onSubmit={e => {
        e.preventDefault();
        void mutate(() => api.put(`${root}/members`, { login: login.trim(), version: 0, isActive: true, manageAccess: manage }));
      }}>
        <h4>Пов’язати наявний технічний логін</h4>
        <p>Це локальна асоціація, не email-запрошення. Обліковий запис і спосіб входу готує системний адміністратор окремо.</p>
        <label>Точний логін<input value={login} maxLength={256} disabled={!canEdit || busy} onChange={e => setLogin(e.target.value)} /></label>
        {context?.systemAdministrator && <label className="operating-groups-check">
          <input type="checkbox" checked={manage} disabled={!canEdit || busy} onChange={e => setManage(e.target.checked)} />
          Керування локальним доступом (не HR і не системне адміністрування)
        </label>}
        <button disabled={!canEdit || busy || !login.trim()}>Пов’язати логін</button>
      </form>
      <div className="operating-groups-create">
        <h4>Локальні учасники</h4>
        {details.members.map(member => <div className="operating-groups-warehouse-row" key={member.userId}>
          <span>{member.login} — {member.isActive ? 'активний' : 'відкликаний'}{member.manageAccess ? ', керування доступом' : ''}</span>
          <button type="button" disabled={!canEdit || busy || (!context?.systemAdministrator && member.manageAccess)}
            onClick={() => saveMember(member, !member.isActive)}>{member.isActive ? 'Відкликати' : 'Відновити без grants'}</button>
          {context?.systemAdministrator && <button type="button" disabled={!canEdit || busy} onClick={() =>
            void mutate(() => api.put(`${root}/members`, {
              login: member.login, version: member.version, isActive: member.isActive, manageAccess: !member.manageAccess,
            }))}>{member.manageAccess ? 'Зняти керування доступом' : 'Надати керування доступом'}</button>}
        </div>)}
        {!details.members.length && <p>Локальних учасників немає. Системний адміністратор має явно призначити першого керівника доступу.</p>}
      </div>
      <form className="operating-groups-create" onSubmit={e => {
        e.preventDefault(); void mutate(() => api.post(`${root}/resources`, {
          ...newResource, code: newResource.resourceType === 'warehouse' ? newResource.code : null,
        }));
      }}>
        <h4>Створити новий локальний ресурс</h4>
        <p>
          Лише новий об’єкт, без копіювання чужих ID чи залишків. Створювач отримує grant лише на цей ресурс.
          Майданчики створюються окремо в управлінні місцями проведення; доступ до них призначається нижче.
        </p>
        <label>Тип<select value={newResource.resourceType} disabled={!canEdit || busy} onChange={e => setNewResource({ ...newResource, resourceType: e.target.value })}>
          <option value="warehouse">Склад</option><option value="kitchen">Виробнича кухня з новим підрозділом</option>
        </select></label>
        <label>Назва ресурсу<input value={newResource.name} maxLength={newResource.resourceType === 'kitchen' ? 100 : 160} disabled={!canEdit || busy} onChange={e => setNewResource({ ...newResource, name: e.target.value })} /></label>
        {newResource.resourceType === 'warehouse' && <label>Код складу<input value={newResource.code} pattern="[A-Za-z0-9_-]{2,40}" required maxLength={40}
          disabled={!canEdit || busy} onChange={e => setNewResource({ ...newResource, code: e.target.value.toUpperCase() })} /></label>}
        <button disabled={!canEdit || busy || !newResource.name.trim()}>Створити локальний ресурс</button>
      </form>
      <div className="operating-groups-create">
        <h4>Доступ до ресурсів</h4>
        <label>Учасник<select value={memberId} disabled={busy} onChange={e => setMemberId(e.target.value)}>
          <option value="">Оберіть учасника</option>
          {details.members.filter(x => x.isActive && (context?.systemAdministrator || !x.manageAccess))
            .map(x => <option key={x.userId} value={x.userId}>{x.login}</option>)}
        </select></label>
        {grants && <>
          <label>Локальний ресурс<select value={resource} disabled={!canEdit || busy} onChange={e => setResource(e.target.value)}>
            <option value="">Оберіть ресурс</option>
            {details.resources.map(x => <option key={`${x.resourceType}:${x.id}`} value={`${x.resourceType}:${x.id}`}>{x.resourceType}: {x.name}</option>)}
          </select></label>
          <button type="button" disabled={!canEdit || busy || !resource} onClick={() => saveGrant()}>Надати доступ із сьогодні</button>
          {grants.grants.map(grant => <div className="operating-groups-warehouse-row" key={grant.id}>
            <span>{details.resources.find(x => x.resourceType === (grant.warehouseId ? 'warehouse' : grant.kitchenId ? 'kitchen' : 'venue') &&
              x.id === (grant.warehouseId ?? grant.kitchenId ?? grant.venueId))?.name ?? 'Ресурс'}: {grant.effectiveFrom} — {grant.cancelled ? 'скасовано' : grant.effectiveTo ?? 'без кінцевої дати'}</span>
            <button type="button" disabled={!canEdit || busy || grant.cancelled || (!!grant.effectiveTo && grant.effectiveTo <= today())}
              onClick={() => saveGrant(grant.id)}>Відкликати grant</button>
          </div>)}
        </>}
      </div>
      <p>Локальні бізнес-налаштування задаються окремо у відповідних модулях. Маршрути повідомлень і secrets — лише на сервері;
        тут немає токенів, адресатів, тестової розсилки або глобального fallback. B52/email/CRM-AI не готові до локального підключення.</p>
    </>}
  </section>;
}
